import { toNumber } from "../../shared/money";

/*
 * How set up an item is, for Home's setup bars and the Materials filters (so their counts match).
 */

export interface SetupGaps {
    /** No supplier price for it. */
    noSupplier: boolean;
    /** No cost entered (nothing, or 0). */
    noCost: boolean;
    /** Its price is typed in: no pricing rule or group works it out. */
    typed: boolean;
}

export function setupGaps(item: { cost: string }, about: { hasSupplierPrice: boolean; hasPricing: boolean }): SetupGaps {
    return {
        noSupplier: !about.hasSupplierPrice,
        noCost: toNumber(item.cost) <= 0,
        typed: !about.hasPricing,
    };
}

/** A name as people tell items apart by it: ignoring capitals, and spaces at either end or doubled. */
export const nameKey = (n: string) => n.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Items in use (for sale, or hidden: on standby) with the same name as another: on a job, a quote or an invoice they can't be
 * told apart. Each such item's uuid -> the others (their item numbers tell them apart).
 */
export function sharedNames<T extends { uuid: string; name: string; item_number: string; active: boolean; hidden: boolean }>(items: readonly T[]): Map<string, T[]> {
    const byKey = Map.groupBy(
        items.filter((i) => (i.active || i.hidden) && nameKey(i.name)),
        (i) => nameKey(i.name),
    );
    const out = new Map<string, T[]>();
    for (const same of byKey.values()) {
        if (same.length < 2) continue;
        for (const i of same) out.set(i.uuid, same.filter((o) => o.uuid !== i.uuid));
    }
    return out;
}

/** An item number as accounting software matches it: ignoring capitals and spaces at either end. */
export const itemNumberKey = (n: string) => n.trim().toLowerCase();

/**
 * Item numbers that can trip up accounting software (an invoice line it can't match to an item
 * creates a new one): items in use (for sale, or hidden: on standby) and active bundles with none, and those sharing one.
 * `clashes`: each such item's uuid -> the names it clashes with.
 */
export function itemNumberIssues(items: readonly { uuid: string; name: string; item_number: string; active: boolean; hidden?: boolean }[]) {
    const active = items.filter((i) => i.active || i.hidden);
    const missing = new Set(active.filter((i) => !i.item_number.trim()).map((i) => i.uuid));
    const byKey = new Map<string, { uuid: string; name: string }[]>();
    for (const i of active) {
        const key = itemNumberKey(i.item_number);
        if (key) byKey.set(key, [...(byKey.get(key) ?? []), i]);
    }
    const clashes = new Map<string, string[]>();
    for (const same of byKey.values()) {
        if (same.length < 2) continue;
        for (const i of same) clashes.set(i.uuid, same.filter((o) => o.uuid !== i.uuid).map((o) => o.name));
    }
    return { missing, clashes };
}

type Dupe = { uuid: string; name: string; item_number: string; active: boolean; hidden: boolean };

/**
 * An item's duplicates: the other items in use sharing its item number or its name (merge.ts merges two into one). None for one
 * not in use.
 */
export function duplicatesOf<T extends Dupe>(items: readonly T[], item: Dupe): T[] {
    if (!item.active && !item.hidden) return [];
    const number = itemNumberKey(item.item_number);
    const name = nameKey(item.name);
    return items.filter((o) => o.uuid !== item.uuid && (o.active || o.hidden) && ((number && itemNumberKey(o.item_number) === number) || (name && nameKey(o.name) === name)));
}

/**
 * Items brought in again by an import in ServiceM8 (its supplier import doesn't see archived ones, so hidden items come in anew):
 * a new item from ServiceM8 (`unknown`: no one has said it's real) sharing its item number with just one known item in use. Each as
 * a merge: keep the known one, the new one merged into it (its cost, the supplier's new one, taken).
 */
export function importedDuplicates<T extends Dupe>(items: readonly T[], unknown: ReadonlySet<string>): { keep: T; remove: T }[] {
    const byNumber = Map.groupBy(
        items.filter((i) => (i.active || i.hidden) && itemNumberKey(i.item_number)),
        (i) => itemNumberKey(i.item_number),
    );
    return [...byNumber.values()].flatMap((same) => {
        const known = same.filter((i) => !unknown.has(i.uuid));
        const keep = known[0];
        if (known.length !== 1 || !keep) return [];
        return same.filter((i) => unknown.has(i.uuid)).map((remove) => ({ keep, remove }));
    });
}
